import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Info } from '@/utils/icons';

interface InfoTooltipProps {
  /** Tooltip title text */
  title: string;
  /** Tooltip body text */
  body: string;
  /** Dark mode flag for styling */
  isDark: boolean;
  /** Tailwind width class (e.g., 'w-72', 'w-80'). Defaults to 'w-72' */
  width?: string;
  /** Icon size in Tailwind units (3, 3.5, 4). Defaults to 3.5 */
  iconSize?: number;
  /** Optional aria-label override. Defaults to "More information" */
  ariaLabel?: string;
  /** Horizontal alignment of the tooltip card. 'left' anchors to left edge, 'right' anchors to right edge. Defaults to 'left' */
  align?: 'left' | 'right';
}

/**
 * Reusable info tooltip component with hover-triggered display.
 * Renders an info icon that shows a styled tooltip card on hover.
 * On mobile (tap), toggles visibility via click. Tap outside to dismiss.
 * Automatically repositions to stay within the viewport.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  body,
  isDark,
  width = 'w-72',
  iconSize = 3.5,
  ariaLabel = 'More information',
  align = 'left',
}) => {
  const iconSizeClass = iconSize === 3 ? 'w-3 h-3' : iconSize === 4 ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [nudge, setNudge] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const reposition = useCallback(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    if (rect.right > window.innerWidth - pad) {
      setNudge(-(rect.right - window.innerWidth + pad));
    } else if (rect.left < pad) {
      setNudge(pad - rect.left);
    } else {
      setNudge(0);
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  // Close on outside tap
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [isOpen]);

  // Reposition when opened via tap
  useEffect(() => {
    if (isOpen) reposition();
  }, [isOpen, reposition]);

  return (
    <span
      ref={wrapperRef}
      className="group/tooltip relative inline-flex text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help shrink-0"
      aria-label={ariaLabel}
      onMouseEnter={reposition}
      onClick={handleClick}
    >
      <Info className={iconSizeClass} />
      {/* Tooltip card - visible on hover or tap */}
      <span
        ref={tooltipRef}
        className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-[9999] mt-1.5 ${width} p-3 rounded-xl border shadow-lg text-left transition-all duration-150 ${
          isOpen
            ? 'opacity-100 visible pointer-events-auto'
            : 'opacity-0 invisible pointer-events-none group-hover/tooltip:opacity-100 group-hover/tooltip:visible group-hover/tooltip:pointer-events-auto'
        } ${
          isDark
            ? 'bg-neutral-900 border-neutral-600 text-neutral-200'
            : 'bg-white border-gray-200 text-gray-900'
        }`}
        style={nudge ? { transform: `translateX(${nudge}px)` } : undefined}
        role="tooltip"
      >
        <div
          className="text-xs font-semibold mb-1.5"
          style={{ color: isDark ? '#F9FAFB' : '#111827' }}
        >
          {title}
        </div>
        <p
          className="text-xs leading-relaxed"
          style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
        >
          {body}
        </p>
      </span>
    </span>
  );
};

export default InfoTooltip;
